import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  EventOrderSummary,
  PaymentPending,
  PayLaterRegistrationSuccess,
  PaidRegistrationSuccess,
  RegistrationClosed,
  type EventCheckoutOrder,
} from '@/components/events/public-event-receipt'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { RegistrationQuestion } from '@/components/events/public-registration-questions'
import { PublicTicketPicker, type TicketType } from '@/components/events/public-ticket-picker'
import { usePublicEventRegistration } from '@/components/events/use-public-event-registration'
import type { VerifiedEventBuyer } from '@/components/events/member-ticket-verification'
import { useMemberVerification } from '@/components/events/use-member-verification'
import { getJSON } from '@/lib/api'
import { RegistrationSuccess } from '@/components/events/public-registration-success'
import { EventCheckoutForm } from '@/components/events/event-checkout-form'
import { PublicEventWaitlist } from '@/components/events/public-event-waitlist'
export interface PublicEventSalesEvent {
  id: number
  name: string
  schedule: string
  time_zone_label: string
  location: string | null
  registration_open: boolean
  sold_out?: boolean
  cancelled: boolean
  ticket_types: TicketType[]
  registration_questions: RegistrationQuestion[]
  pay_later_available?: boolean
  pay_later_due_on?: string
  waitlist_when_full?: boolean
}
interface EventCheckoutConfirmationResponse {
  confirmation: { status: 'pending' | 'payment_received' }
  order: EventCheckoutOrder | null
}
type Selection = { ticket: TicketType; quantity: number }
export function PublicEventSales({
  event,
  onCheckoutChange,
  memberVerification: suppliedMemberVerification,
  memberSessionVerified = false,
  onMemberVerification,
}: {
  event: PublicEventSalesEvent
  onCheckoutChange: (active: boolean) => void
  memberVerification?: VerifiedEventBuyer | null
  memberSessionVerified?: boolean
  onMemberVerification?: (verification: VerifiedEventBuyer) => void
}) {
  const checkoutSessionId = new URLSearchParams(window.location.search).get(
    'event_checkout_session',
  )
  const confirmation = useEventCheckoutConfirmation(event.id, checkoutSessionId)
  const [quantities, setQuantities] = React.useState<Record<number, number>>({})
  const { memberVerification, handleMemberVerification } = useMemberVerification(
    suppliedMemberVerification,
    onMemberVerification,
  )
  const [checkoutQuantities, setCheckoutQuantities] = React.useState<Record<number, number> | null>(
    null,
  )
  const [waitlistTicket, setWaitlistTicket] = React.useState<TicketType | null>(null)
  const selections = selectedTickets(event.ticket_types, checkoutQuantities)
  if (checkoutSessionId && confirmation.data?.order) {
    return <PaidRegistrationSuccess event={event} order={confirmation.data.order} />
  }
  if (checkoutSessionId && (confirmation.isLoading || confirmation.data)) {
    return <PaymentPending />
  }
  if (checkoutQuantities && selections.length > 0) {
    return (
      <CheckoutLayout
        event={event}
        selections={selections}
        memberVerification={memberVerification}
        onBack={() => {
          setCheckoutQuantities(null)
          onCheckoutChange(false)
        }}
      />
    )
  }
  if (event.cancelled) return null
  return (
    <Card className="sticky top-6 h-fit overflow-hidden">
      <CardHeader className="border-b border-border px-4 py-3">
        <CardTitle className="text-body-sm">
          {event.registration_open ? 'Tickets' : 'Registration'}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <SalesCardContent
          event={event}
          quantities={quantities}
          setQuantities={setQuantities}
          waitlistTicket={waitlistTicket}
          setWaitlistTicket={setWaitlistTicket}
          memberVerification={memberVerification}
          memberSessionVerified={memberSessionVerified}
          onMemberVerification={handleMemberVerification}
          onCheckoutChange={onCheckoutChange}
          setCheckoutQuantities={setCheckoutQuantities}
        />
      </CardContent>
    </Card>
  )
}

function SalesCardContent({
  event,
  quantities,
  setQuantities,
  waitlistTicket,
  setWaitlistTicket,
  memberVerification,
  memberSessionVerified,
  onMemberVerification,
  onCheckoutChange,
  setCheckoutQuantities,
}: {
  event: PublicEventSalesEvent
  quantities: Record<number, number>
  setQuantities: React.Dispatch<React.SetStateAction<Record<number, number>>>
  waitlistTicket: TicketType | null
  setWaitlistTicket: (ticket: TicketType) => void
  memberVerification: VerifiedEventBuyer | null
  memberSessionVerified: boolean
  onMemberVerification: (verification: VerifiedEventBuyer) => void
  onCheckoutChange: (active: boolean) => void
  setCheckoutQuantities: (quantities: Record<number, number>) => void
}) {
  if (event.waitlist_when_full && event.sold_out)
    return <PublicEventWaitlist eventId={event.id} ticketTypes={event.ticket_types} />
  if (waitlistTicket)
    return (
      <PublicEventWaitlist
        eventId={event.id}
        ticketTypes={event.ticket_types}
        initialTicket={waitlistTicket}
      />
    )
  if (!event.registration_open)
    return event.sold_out ? (
      <PublicTicketPicker
        event={event}
        quantities={{}}
        setQuantities={() => undefined}
        onContinue={() => undefined}
        showCta={false}
        onJoinWaitlist={waitlistJoinHandler(event.waitlist_when_full, setWaitlistTicket)}
      />
    ) : (
      <RegistrationClosed />
    )
  return (
    <PublicTicketPicker
      event={event}
      quantities={quantities}
      setQuantities={setQuantities}
      onContinue={(selected) => {
        setCheckoutQuantities(selected)
        onCheckoutChange(true)
      }}
      memberVerification={memberVerification}
      memberSessionVerified={memberSessionVerified}
      onMemberVerification={onMemberVerification}
      onJoinWaitlist={waitlistJoinHandler(event.waitlist_when_full, setWaitlistTicket)}
    />
  )
}

export function waitlistJoinHandler(
  enabled: boolean | undefined,
  handler: (ticket: TicketType) => void,
) {
  return enabled ? handler : undefined
}

function useEventCheckoutConfirmation(eventId: number, processorSessionId: string | null) {
  return useQuery({
    queryKey: ['event-card-checkout-confirmation', eventId, processorSessionId],
    queryFn: () =>
      getJSON<EventCheckoutConfirmationResponse>(
        `/api/v1/public/events/${eventId}/card_checkouts/confirmation?processor_session_id=${encodeURIComponent(processorSessionId ?? '')}`,
      ),
    enabled: processorSessionId !== null,
    refetchInterval: (query) =>
      query.state.data?.confirmation.status === 'pending' ? 1_000 : false,
  })
}

function selectedTickets(ticketTypes: TicketType[], quantities: Record<number, number> | null) {
  return ticketTypes
    .filter((ticket) => (quantities?.[ticket.id] ?? 0) > 0)
    .map((ticket) => ({ ticket, quantity: quantities?.[ticket.id] ?? 0 }))
}

function CheckoutLayout({
  event,
  selections,
  memberVerification,
  onBack,
}: {
  event: PublicEventSalesEvent
  selections: Selection[]
  memberVerification: VerifiedEventBuyer | null
  onBack: () => void
}) {
  const formId = `event-checkout-${event.id}`
  const [paymentMethod, setPaymentMethod] = React.useState<'online' | 'invoice'>('online')
  const [discountCode, setDiscountCode] = React.useState<string | null>(null)
  const [quotedTotalCents, setQuotedTotalCents] = React.useState<number | null>(null)
  const [payLaterOrder, setPayLaterOrder] = React.useState<EventCheckoutOrder | null>(null)
  if (payLaterOrder) {
    return (
      <div className="lg:col-span-2">
        <PayLaterRegistrationSuccess event={event} order={payLaterOrder} />
      </div>
    )
  }
  return (
    <div className="space-y-4 lg:col-span-2">
      <div className="flex items-center gap-2 text-caption text-muted-foreground">
        <Button
          type="button"
          variant="outline"
          className="h-8 shrink-0 border-0 bg-transparent px-0 text-caption text-muted-foreground hover:bg-transparent hover:text-foreground"
          onClick={onBack}
        >
          ← Back to tickets
        </Button>
        <span aria-hidden="true">·</span>
        <p className="truncate">
          {event.name} · {event.schedule} {event.time_zone_label}
        </p>
      </div>
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_380px]">
        <Checkout
          event={event}
          selections={selections}
          memberVerification={memberVerification}
          formId={formId}
          paymentMethod={paymentMethod}
          discountCode={discountCode}
          quotedTotalCents={quotedTotalCents}
          onPaymentMethodChange={setPaymentMethod}
          onPayLaterOrder={setPayLaterOrder}
        />
        <EventOrderSummary
          eventId={event.id}
          selections={selections}
          formId={formId}
          paymentMethod={paymentMethod}
          memberVerificationToken={memberVerification?.token ?? null}
          discountCode={discountCode}
          onDiscountCodeChange={setDiscountCode}
          onQuoteChange={setQuotedTotalCents}
        />
      </div>
    </div>
  )
}

function Checkout({
  event,
  selections,
  memberVerification,
  formId,
  paymentMethod,
  discountCode,
  quotedTotalCents,
  onPaymentMethodChange,
  onPayLaterOrder,
}: {
  event: PublicEventSalesEvent
  selections: Selection[]
  memberVerification: VerifiedEventBuyer | null
  formId: string
  paymentMethod: 'online' | 'invoice'
  discountCode: string | null
  quotedTotalCents: number | null
  onPaymentMethodChange: (value: 'online' | 'invoice') => void
  onPayLaterOrder: (order: EventCheckoutOrder) => void
}) {
  const quantity = selections.reduce((total, selection) => total + selection.quantity, 0)
  const requiresPayment = selections.some(({ ticket }) => ticket.price_cents > 0)
  const attendeeLabels = selections.flatMap(({ ticket, quantity: selectedQuantity }) =>
    Array.from({ length: selectedQuantity }, () => ticket.name),
  )
  const registration = usePublicEventRegistration({
    eventId: event.id,
    selections: selections.map(({ ticket, quantity: selectedQuantity }) => ({
      ticket_type_id: ticket.id,
      quantity: selectedQuantity,
    })),
    registrationQuestions: event.registration_questions ?? [],
    requiresPayment,
    paymentMethod,
    memberVerification,
    discountCode,
    quotedTotalCents,
  })

  if (!requiresPayment && registration.register.isSuccess) {
    return <RegistrationSuccess quantity={quantity} />
  }
  return (
    <Card className="h-fit overflow-hidden">
      <CardContent className="p-0">
        <EventCheckoutForm
          formId={formId}
          attendeeLabels={attendeeLabels}
          memberVerification={memberVerification}
          requiresPayment={requiresPayment}
          registration={registration}
          payLaterAvailable={event.pay_later_available === true}
          paymentMethod={paymentMethod}
          onPaymentMethodChange={onPaymentMethodChange}
          payLaterDueOn={event.pay_later_due_on}
          onPayLaterOrder={onPayLaterOrder}
        />
      </CardContent>
    </Card>
  )
}
